// Quote Builder — floating plan/elevation thumbnail (bottom-left).
//
// Steve, 2026-09-10: while quoting he was flicking between browser windows to
// look at the plan, because the portal was not full-screen. This keeps the
// job's images on screen without leaving the Quote Builder.
//
// ── Three states, on purpose ──────────────────────────────────────────────
//   collapsed  a thin edge tab. Remembered, see COLLAPSED_KEY.
//   thumbnail  ~88px tile, the lead image plus a count badge. Grows on HOVER
//              via CSS alone — no JS, so it cannot lag the pointer.
//   pinned     a NON-MODAL panel with prev/next, and a Full size button that
//              hands off to the shared lightbox.
//
// WHY PINNED IS NOT THE LIGHTBOX. The obvious reuse would be to click straight
// through to `openLightbox`, and that was the first plan. But the lightbox is
// modal — it calls lockScroll() and covers the viewport with an `inset: 0`
// overlay — so with it open the retailer cannot scroll or edit the quote,
// which is the one thing this feature exists to let them do. So the pinned
// state is its own small panel, and the lightbox stays for the "actually look
// closely at it" step it is good at. Steve's call, 2026-09-10.
//
// WHY CLICKING ELSEWHERE DOES NOT CLOSE IT. Normal for a popover, wrong here:
// every click in the Quote Builder is the retailer working, and a panel that
// closed on the next click would never survive long enough to be referenced.
// Closing is explicit — the close button, or Escape.
//
// ── Data ──────────────────────────────────────────────────────────────────
// `fetchDesigns` from plans-panel.ts, so this shows exactly what the PLANS tab
// shows for the same customer: design_files rows plus the customer's wizard
// room photos, scoped server-side by RLS. Not just the designs attached to
// this quote — Steve asked for anything uploaded for the job.
//
// Non-images are filtered out with `isImageUrl`, which matters more than it
// sounds: 117 of the 180 design files on this install are PDFs. A job whose
// only upload is a PDF plan therefore shows NO thumbnail, which is correct —
// better than a broken tile — but it is the reason this can be absent on a
// job that visibly has files on the Plans tab.
//
// ── Not customer-facing ───────────────────────────────────────────────────
// `visibility` gates the CUSTOMER PDF only; the Plans tab already lists every
// file regardless, and this is the same retailer-authed view of the same rows.
// Nothing here is reachable by a customer and pdf.ts knows nothing about it —
// asserted in verify-qb-layout-dom.

import { iconMarkup } from "@tkos/shared-ui";
import { openLightbox, isImageUrl, type LightboxImage } from "../../shell/lightbox";
import { fetchDesigns, type Design, type StoredKind } from "./plans-panel";
import type { QuoteStore } from "./state";
import { escapeHtml, svgIcon } from "./formatters";

/** Collapsed/expanded is a UI PREFERENCE — which is why it is allowed in
 *  localStorage. Nothing about the job or the quote is stored here. */
const COLLAPSED_KEY = "qb_floating_plan_collapsed";

/** Reading order for the carousel, and so also which image leads.
 *  Plan first means the lead tile is the most recent PLAN where the job has
 *  one, falling back to the newest image of any kind — which is what Steve
 *  asked to see without having to choose. */
const KIND_ORDER: StoredKind[] = ["Plan", "Elevation", "3D Image", "Other"];

const CHEVRON_LEFT = "M15 18l-6-6 6-6";
const CHEVRON_RIGHT = "M9 6l6 6-6 6";
const CHEVRON_UP = "M6 15l6-6 6 6";

function kindRank(k: StoredKind): number {
  const i = KIND_ORDER.indexOf(k);
  return i === -1 ? KIND_ORDER.length : i;
}

/** Images only, in reading order: by kind, then newest first within a kind. */
function orderImages(designs: Design[]): Design[] {
  return designs
    .filter((d) => !!d.dataUrl && isImageUrl(d.dataUrl) )
    .sort((a, b) => kindRank(a.kind) - kindRank(b.kind) || b.uploadedAt - a.uploadedAt);
}

function toLightbox(images: Design[]): LightboxImage[] {
  return images.map((d) => ({ url: d.dataUrl, name: d.name || d.kind, type: d.kind }));
}

export function mountFloatingPlan(host: HTMLElement, store: QuoteStore): () => void {
  let destroyed = false;
  let images: Design[] = [];
  let index = 0;
  let pinned = false;
  let collapsed = readCollapsed();
  // The customer the gallery is scoped to, so the store subscription only
  // re-fetches when the assigned customer actually changes. Mirrors
  // plans-panel's sentinel; " " is "never loaded", distinct from "" (no
  // customer assigned, which is a real state that shows nothing).
  let loadedCustomerId = " ";

  function readCollapsed(): boolean {
    try { return host.ownerDocument.defaultView?.localStorage.getItem(COLLAPSED_KEY) === "1"; }
    catch { return false; }
  }
  function writeCollapsed(v: boolean): void {
    try {
      const ls = host.ownerDocument.defaultView?.localStorage;
      if (v) ls?.setItem(COLLAPSED_KEY, "1"); else ls?.removeItem(COLLAPSED_KEY);
    } catch { /* private mode / storage disabled — the preference just won't stick */ }
  }

  async function refresh(): Promise<void> {
    const cid = store.get().customerId ?? "";
    loadedCustomerId = cid;
    const next = await fetchDesigns(cid);
    if (destroyed) return;
    // The customer may have changed mid-fetch; a late reply must not paint
    // one job's plans over another's.
    if ((store.get().customerId ?? "") !== cid) return;
    images = orderImages(next);
    if (index >= images.length) index = 0;
    render();
  }

  function render(): void {
    if (destroyed) return;
    // Nothing to show, nothing to render. No empty shell and no "no plans
    // yet" prompt: this is an ambient convenience, and a permanent empty box
    // in the corner of every quote would be worse than its absence.
    if (!images.length) { host.innerHTML = ""; return; }

    const lead = images[Math.min(index, images.length - 1)]!;
    const total = images.length;

    if (collapsed) {
      host.innerHTML = `
        <div class="qb-fplan qb-fplan--collapsed" data-role="fplan">
          <button type="button" class="qb-fplan__tab" data-role="fplan-expand"
                  title="Show the job's plans" aria-label="Show the job's plans (${total})">
            ${svgIcon(CHEVRON_UP)}<span class="qb-fplan__tab-n">${total}</span>
          </button>
        </div>`;
      wire();
      return;
    }

    host.innerHTML = `
      <div class="qb-fplan${pinned ? " is-pinned" : ""}" data-role="fplan">
        ${pinned ? `
        <div class="qb-fplan__panel" data-role="fplan-panel" role="group"
             aria-label="Job plans, ${index + 1} of ${total}">
          <div class="qb-fplan__head">
            <span class="qb-fplan__kind">${escapeHtml(lead.kind)}</span>
            <span class="qb-fplan__count">${index + 1} of ${total}</span>
            <button type="button" class="qb-fplan__act" data-role="fplan-full"
                    title="Open full size" aria-label="Open full size">${iconMarkup("fullscreen")}</button>
            <button type="button" class="qb-fplan__act" data-role="fplan-close"
                    title="Close" aria-label="Close plans panel">${iconMarkup("close")}</button>
          </div>
          <div class="qb-fplan__stage">
            <img src="${escapeHtml(lead.dataUrl)}" alt="${escapeHtml(lead.name || lead.kind)}" />
          </div>
          <div class="qb-fplan__foot">
            <button type="button" class="qb-fplan__nav" data-role="fplan-prev"
                    ${total > 1 ? "" : "disabled"} aria-label="Previous image">${svgIcon(CHEVRON_LEFT)}</button>
            <span class="qb-fplan__name" title="${escapeHtml(lead.name)}">${escapeHtml(lead.name || lead.kind)}</span>
            <button type="button" class="qb-fplan__nav" data-role="fplan-next"
                    ${total > 1 ? "" : "disabled"} aria-label="Next image">${svgIcon(CHEVRON_RIGHT)}</button>
          </div>
        </div>` : `
        <button type="button" class="qb-fplan__thumb" data-role="fplan-open"
                title="${escapeHtml(lead.name || lead.kind)} — click to pin open"
                aria-label="Show the job's plans (${total})">
          <img src="${escapeHtml(lead.dataUrl)}" alt="" />
          ${total > 1 ? `<span class="qb-fplan__badge">${total}</span>` : ""}
          <span class="qb-fplan__hint">${escapeHtml(lead.kind)}</span>
        </button>
        <button type="button" class="qb-fplan__collapse" data-role="fplan-collapse"
                title="Tuck away" aria-label="Tuck the plans thumbnail away">${svgIcon(CHEVRON_LEFT)}</button>`}
      </div>`;
    wire();
  }

  function step(by: number): void {
    if (!images.length) return;
    index = (index + by + images.length) % images.length;
    render();
  }

  function setPinned(v: boolean): void {
    pinned = v;
    // Escape closes the panel, and the listener exists ONLY while it is open —
    // an always-on document handler in a screen this busy would be one more
    // thing quietly swallowing keys. Bound to host.ownerDocument rather than
    // the global `document` so the module never reaches outside what it was
    // handed (the rule verify-qb-layout-dom section 7 pins for the pickers).
    const doc = host.ownerDocument;
    if (v) doc.addEventListener("keydown", onKeydown);
    else doc.removeEventListener("keydown", onKeydown);
    render();
  }

  function onKeydown(e: KeyboardEvent): void {
    if (e.key === "Escape") { e.preventDefault(); setPinned(false); return; }
    if (e.key === "ArrowLeft") { e.preventDefault(); step(-1); return; }
    if (e.key === "ArrowRight") { e.preventDefault(); step(1); }
  }

  function wire(): void {
    const on = (role: string, fn: () => void): void => {
      host.querySelector<HTMLElement>(`[data-role='${role}']`)
        ?.addEventListener("click", (e) => { e.preventDefault(); fn(); });
    };
    on("fplan-open", () => setPinned(true));
    on("fplan-close", () => setPinned(false));
    on("fplan-prev", () => step(-1));
    on("fplan-next", () => step(1));
    on("fplan-collapse", () => { collapsed = true; writeCollapsed(true); render(); });
    on("fplan-expand", () => { collapsed = false; writeCollapsed(false); render(); });
    // The one place the modal viewer is right: the retailer has stopped
    // quoting and wants to look properly.
    on("fplan-full", () => { openLightbox({ images: toLightbox(images), startIndex: index }); });
  }

  void refresh();

  const unsub = store.subscribe(() => {
    if (destroyed) return;
    if ((store.get().customerId ?? "") !== loadedCustomerId) {
      // A different job: drop to the lead image so the panel cannot open on
      // the previous customer's third elevation.
      index = 0;
      pinned = false;
      host.ownerDocument.removeEventListener("keydown", onKeydown);
      void refresh();
    }
  });

  return () => {
    destroyed = true;
    unsub();
    host.ownerDocument.removeEventListener("keydown", onKeydown);
    host.innerHTML = "";
  };
}
